import {
  User,
  IdCard,
  ChevronsLeft,
  type LucideIcon,
  Calendar,
  FolderOpen,
  ListChecksIcon,
  ClipboardCheck,
  Target,
  UsersRound,
  MessageSquare,
  UserSearch,
} from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { KollabHRBadgeFullLogo, KollabHRBadgeLogo } from '@gokrew/ui/assets';
import { CompanySwitcher, CountBadge, useMessaging } from '@gokrew/ui';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@gokrew/ui/components/tooltip';
import { useMediaQuery } from '@gokrew/ui/hooks';
import { cn } from '@gokrew/ui/lib/utils';
import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import type { EmployeePermissionScope } from '@gokrew/models/employee-permissions';
import { permissionKeys } from '@/utility/permissions';
import { PermissionGuard } from './PermissionGuard';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { usePermissions } from '@/context/PermissionContext';
import {
  useCanSeeApprovals,
  useMyTeamApprovalsCount,
  useMyTeamChangeRequestsCount,
  useMyTeamTimesheetApprovalsCount,
  useMyTeamAccess,
} from '@/hooks/my-team/useMyTeam';
import { usePerformanceEnabled } from '@/hooks/performance/usePerformanceSummary';
import { useCandidateReviewSummary } from '@/hooks/candidate-reviews/useCandidateReviews';
import styles from './Sidebar.module.css';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { TranslationNS } from '@gokrew/models/constants';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

interface MenuItem {
  nameKey: string;
  icon: LucideIcon;
  href: string;
  scope?: EmployeePermissionScope | EmployeePermissionScope[];
  /** Pending count shown beside the label (e.g. approvals waiting). */
  badge?: number;
  /** Screen-reader text for the badge; defaults to pending approvals. */
  badgeLabelKey?: string;
  /** Small label beside the name, e.g. Beta for a feature still being tested. */
  tagKey?: string;
}

interface NavSection {
  id: string;
  labelKey: string;
  items: MenuItem[];
}

const getNavSections = (
  isManager: boolean,
  canApprove: boolean,
  pendingApprovals: number,
  performanceOn: boolean,
  unreadMessages: number,
  candidateReviews: { total: number; waiting: number } | undefined
): NavSection[] => [
  {
    id: 'overview',
    labelKey: 'sections.overview',
    items: [
      {
        nameKey: 'workspace.home',
        icon: User,
        href: '/dashboard',
        scope: permissionKeys.dashboard.read,
      },
      {
        nameKey: 'nav.messages',
        icon: MessageSquare,
        href: '/messages',
        badge: unreadMessages,
        badgeLabelKey: 'messaging_unread',
      },
      { nameKey: 'workspace.todo', icon: ListChecksIcon, href: '/tasks' },
      { nameKey: 'workspace.file', icon: IdCard, href: '/my-information' },
      {
        nameKey: 'workspace.leave',
        icon: Calendar,
        href: '/leave-management',
        scope: permissionKeys.leave.read,
      },
      // My performance: reviews, goals and 1:1s, only while the module is on
      // (performance spec PF-21).
      ...(performanceOn
        ? [
            {
              nameKey: 'nav.my_performance',
              icon: Target,
              href: '/performance/reviews',
              // Beta until JP calls Performance fully tested (2026-10-15).
              tagKey: 'nav.beta',
            },
          ]
        : []),
      ...(isManager
        ? [{ nameKey: 'nav.my_team', icon: UsersRound, href: '/my-team' }]
        : []),
      // Candidates HR shared with me: only once I have one (2026-10-29).
      ...(candidateReviews?.total
        ? [
            {
              nameKey: 'nav.candidate_reviews',
              icon: UserSearch,
              href: '/candidate-reviews',
              badge: candidateReviews.waiting,
              badgeLabelKey: 'nav.candidate_reviews_waiting',
            },
          ]
        : []),
      ...(canApprove
        ? [
            {
              nameKey: 'workspace.approvals',
              icon: ClipboardCheck,
              href: '/my-team/approvals',
              badge: pendingApprovals,
            },
          ]
        : []),
      {
        nameKey: 'workspace.resources',
        icon: FolderOpen,
        href: '/files',
        scope: permissionKeys.file_management.read,
      },
    ],
  },
];

function hasScopeAccess(
  scope: MenuItem['scope'],
  permissions: Set<Partial<EmployeePermissionScope>>
) {
  const scopes =
    scope === undefined ? [] : Array.isArray(scope) ? scope : [scope];
  if (scopes.length === 0) return true;
  return scopes.some((_scope) => permissions.has(_scope));
}

function hasVisibleItems(
  items: MenuItem[],
  permissions: Set<Partial<EmployeePermissionScope>>
) {
  return items.some((item) => hasScopeAccess(item.scope, permissions));
}

interface SidebarNavItemProps {
  item: MenuItem;
  isCollapsedView: boolean;
  t: TFunction<TranslationNS.Sidebar>;
}

function SidebarNavItem({ item, isCollapsedView, t }: SidebarNavItemProps) {
  const location = useLocation();
  const teamSection =
    (item.href === '/my-team' &&
      location.pathname.startsWith('/my-team/') &&
      !location.pathname.startsWith('/my-team/approvals')) ||
    // My performance covers its Reviews and 1:1s tabs.
    (item.href === '/performance/reviews' &&
      location.pathname.startsWith('/performance/') &&
      !location.pathname.startsWith('/performance/manager-reviews'));
  const navLink = (
    <NavLink
      to={item.href}
      end={item.href === '/my-team'}
      className={({ isActive }) =>
        cn(
          styles.navLink,
          (isActive || teamSection) && styles.navLinkActive,
          isCollapsedView && styles.navLinkCollapsed
        )
      }
    >
      <span className={styles.navIconWrap}>
        <item.icon size={16} className={styles.navIcon} />
        {isCollapsedView && !!item.badge && (
          <CountBadge count={item.badge} className={styles.navBadgeCollapsed} />
        )}
      </span>

      {!isCollapsedView && (
        <span className={styles.navLabel}>{t(item.nameKey)}</span>
      )}
      {!isCollapsedView && item.tagKey && (
        <span className={styles.navTag}>{t(item.tagKey)}</span>
      )}
      {!isCollapsedView && !!item.badge && (
        <CountBadge count={item.badge} className={styles.navBadge} />
      )}
      {!!item.badge && (
        <span className="sr-only">
          {t(item.badgeLabelKey ?? 'nav.pending_approvals', {
            count: item.badge,
          })}
        </span>
      )}
    </NavLink>
  );

  return (
    <PermissionGuard scope={item.scope}>
      <li>
        {isCollapsedView ? (
          <Tooltip>
            <TooltipTrigger asChild>{navLink}</TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={8}
              className={cn(
                styles.tooltipContent,
                'border-none bg-transparent text-inherit shadow-none'
              )}
              arrowClassName="!bg-[var(--brand-primary)] !fill-[var(--brand-primary)]"
            >
              {t(item.nameKey)}
            </TooltipContent>
          </Tooltip>
        ) : (
          navLink
        )}
      </li>
    </PermissionGuard>
  );
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useEmployeePortalAuth();
  const company = user?.company;
  const { t } = useTranslation(TranslationNS.Sidebar);
  const { permissions } = usePermissions();
  const canSeeApprovals = useCanSeeApprovals();
  const { data: count = 0 } = useMyTeamApprovalsCount();
  const { data: changeCount = 0 } = useMyTeamChangeRequestsCount();
  const { data: timesheetCount = 0 } = useMyTeamTimesheetApprovalsCount();
  const { data: access } = useMyTeamAccess();
  const isManager = !!access?.enabled && !!access.isManager;
  // Profile changes routed to a manager count too (change rules), and the
  // timesheet weeks and changes routed to them.
  const pendingApprovals =
    (canSeeApprovals ? count + timesheetCount : 0) + changeCount;
  const performanceOn = usePerformanceEnabled();
  const { unreadCount: unreadMessages } = useMessaging();
  const { data: candidateReviews } = useCandidateReviewSummary();
  const sections = getNavSections(
    isManager,
    canSeeApprovals || changeCount > 0,
    pendingApprovals,
    performanceOn,
    unreadMessages,
    candidateReviews
  );

  const isCollapsedRef = useRef(isCollapsed);
  const onToggleRef = useRef(onToggle);
  isCollapsedRef.current = isCollapsed;
  onToggleRef.current = onToggle;

  useEffect(() => {
    if (isMobile && !isCollapsedRef.current) {
      onToggleRef.current();
    }
  }, [location.pathname, isMobile]);

  const toggleHandleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const TOGGLE_HANDLE_EDGE_MARGIN = 20;

  const handleSidebarMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      const handle = toggleHandleRef.current;
      if (!handle) return;

      if (navRef.current?.contains(event.target as Node)) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const y = Math.min(
        Math.max(event.clientY - rect.top, TOGGLE_HANDLE_EDGE_MARGIN),
        rect.height - TOGGLE_HANDLE_EDGE_MARGIN
      );
      handle.style.top = `${y}px`;
    },
    []
  );

  const resetToggleHandlePosition = useCallback(() => {
    const handle = toggleHandleRef.current;
    if (handle) handle.style.top = '';
  }, []);

  return (
    <>
      {!isCollapsed && <div className={styles.overlay} onClick={onToggle} />}

      <aside
        onMouseLeave={resetToggleHandlePosition}
        onMouseMove={handleSidebarMouseMove}
        className={cn(
          styles.sidebar,
          isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded
        )}
      >
        <button
          ref={toggleHandleRef}
          type="button"
          onClick={() => {
            resetToggleHandlePosition();
            onToggle();
          }}
          className={styles.toggleHandle}
          aria-label={isCollapsed ? t('toggle.expand') : t('toggle.collapse')}
          title={isCollapsed ? t('toggle.expand') : t('toggle.collapse')}
          aria-expanded={!isCollapsed}
        >
          <ChevronsLeft
            size={14}
            className={cn(
              styles.toggleHandleIcon,
              isCollapsed && styles.toggleHandleIconCollapsed
            )}
          />
        </button>

        <div className={styles.header}>
          <div className={styles.headerInner}>
            {company && (
              <CompanySwitcher
                companies={[company]}
                selectedCompanyId={company.id}
                onCompanyChange={() => {}}
                isCollapsed={isCollapsed}
                className={styles.companySection}
                flat
              />
            )}
          </div>
        </div>

        <nav ref={navRef} className={styles.nav}>
          {sections.map((section, sectionIndex) => {
            if (!hasVisibleItems(section.items, permissions)) return null;

            return (
              <div
                key={section.id}
                className={cn(
                  styles.navSection,
                  sectionIndex > 0 && styles.navSectionSpaced
                )}
                style={
                  {
                    '--section-accent': `var(--sidebar-accent-${section.id}, var(--brand-primary))`,
                  } as CSSProperties
                }
              >
                <ul className={styles.navList}>
                  {section.items.map((item) => (
                    <SidebarNavItem
                      key={item.href}
                      item={item}
                      isCollapsedView={isCollapsed}
                      t={t}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className={styles.logoSection}>
          {isCollapsed ? (
            <button
              className={styles.logoBtnSmall}
              onClick={() => navigate('/dashboard')}
            >
              <img src={KollabHRBadgeLogo} alt="KollabHR" />
            </button>
          ) : (
            <button
              className={cn(styles.logoBtn, styles.logoBtnReveal)}
              onClick={() => navigate('/dashboard')}
            >
              <img src={KollabHRBadgeFullLogo} alt="KollabHR" />
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
